<script setup lang="ts">
/**
 * RepRates — Shield and armor repair rate display
 */
import type { DefenseStats } from '../../../shared/types';

const props = defineProps<{
  defense: DefenseStats;
}>();

function fmt(n: number): string {
  if (n === 0) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return n.toFixed(1);
}
</script>

<template>
  <div class="rep-rates">
    <div class="rep-row" v-if="props.defense.tank.shieldBoost > 0">
      <span class="rep-label shield">Shield Boost</span>
      <span class="rep-value">{{ fmt(props.defense.tank.shieldBoost) }} HP/s</span>
    </div>
    <div class="rep-row" v-if="props.defense.tank.passiveShield > 0">
      <span class="rep-label shield">Passive Recharge</span>
      <span class="rep-value">{{ fmt(props.defense.tank.passiveShield) }} HP/s</span>
    </div>
    <div class="rep-row" v-if="props.defense.shield.rechargeTime && props.defense.shield.rechargeTime > 0">
      <span class="rep-label shield">Shield Recharge</span>
      <span class="rep-value">{{ (props.defense.shield.rechargeTime).toFixed(0) }}s</span>
    </div>
    <div class="rep-row" v-if="props.defense.tank.armorRepair > 0">
      <span class="rep-label armor">Armor Repair</span>
      <span class="rep-value">{{ fmt(props.defense.tank.armorRepair) }} HP/s</span>
    </div>
    <div class="rep-row" v-if="props.defense.remoteRep.shieldBoost > 0">
      <span class="rep-label shield">Remote Shield</span>
      <span class="rep-value">{{ fmt(props.defense.remoteRep.shieldBoost) }} HP/s</span>
    </div>
    <div class="rep-row" v-if="props.defense.remoteRep.armorRepair > 0">
      <span class="rep-label armor">Remote Armor</span>
      <span class="rep-value">{{ fmt(props.defense.remoteRep.armorRepair) }} HP/s</span>
    </div>
    <div class="rep-row" v-if="props.defense.tank.shieldBoost === 0 && props.defense.tank.armorRepair === 0 && props.defense.tank.passiveShield === 0">
      <span class="rep-label">No active tank</span>
    </div>
  </div>
</template>

<style scoped>
.rep-rates {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rep-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.rep-label {
  font-size: 0.85rem;
  color: var(--text-secondary);
}

.rep-label.shield { color: var(--shield-color); }
.rep-label.armor { color: var(--armor-color); }

.rep-value {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}
</style>
